// src/pages/Dashboard/Admin/Votes/components/VoteQuestionsDrawer.jsx

import React, { Fragment, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  addChoiceToQuestion,
  addQuestionToVote,
  deleteChoice,
  deleteQuestion,
  getVoteById,
  updateQuestion,
} from "../../../../../services/voteServices";

import { toast } from "react-toastify";
import {
  FaListUl,
  FaPlus,
  FaQuestionCircle,
  FaSave,
  FaTimes,
  FaTrash,
  FaEdit,
} from "react-icons/fa";

import {
  QUESTION_TYPES,
  getQuestionTypeLabel,
  isEmptyHtml,
} from "../utils/votesDashboardUtils";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "list",
  "bullet",
  "link",
  "image",
];

function ActionButton({ title, onClick, disabled, children, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.base
      }`}
    >
      {children}
    </button>
  );
}

export default function VoteQuestionsDrawer({ isOpen, vote, onClose, onUpdated }) {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ question_text: "", type: "single" });
  const [editingId, setEditingId] = useState(null);

  const [choiceForms, setChoiceForms] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [savingChoiceId, setSavingChoiceId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const voteId = vote?.id;

  const fetchQuestions = async () => {
    if (!voteId) return;

    try {
      setLoading(true);

      const data = await getVoteById(voteId);
      setQuestions(Array.isArray(data?.questions) ? data.questions : []);
    } catch (error) {
      console.error("Erreur chargement questions :", error);
      toast.error("Erreur lors du chargement des questions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !voteId) return;

    setQuestions([]);
    setForm({ question_text: "", type: "single" });
    setEditingId(null);
    setChoiceForms({});

    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, voteId]);

  const canSubmitQuestion = useMemo(() => {
    return (
      !savingQuestion &&
      !isEmptyHtml(form.question_text) &&
      ["single", "multiple", "open"].includes(form.type)
    );
  }, [form, savingQuestion]);

  const resetQuestionForm = () => {
    setForm({ question_text: "", type: "single" });
    setEditingId(null);
  };

  const handleSubmitQuestion = async (event) => {
    event.preventDefault();

    if (!canSubmitQuestion) {
      toast.warn("Veuillez saisir une question.");
      return;
    }

    try {
      setSavingQuestion(true);

      if (editingId) {
        await updateQuestion(editingId, {
          question_text: form.question_text,
          type: form.type,
        });

        toast.success("Question modifiée.");
      } else {
        await addQuestionToVote(voteId, {
          question_text: form.question_text,
          type: form.type,
        });

        toast.success("Question ajoutée.");
      }

      resetQuestionForm();
      await fetchQuestions();
      await onUpdated?.();
    } catch (error) {
      console.error("Erreur enregistrement question :", error);
      toast.error("Erreur lors de l’enregistrement de la question.");
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingId(question.id);
    setForm({
      question_text: question.question_text || "",
      type: question.type || "single",
    });
  };

  const handleDeleteQuestion = async (question) => {
    if (!question?.id) return;

    if (!window.confirm("Supprimer cette question et ses réponses ?")) return;

    try {
      setDeletingId(`question-${question.id}`);

      await deleteQuestion(question.id);

      toast.success("Question supprimée.");
      await fetchQuestions();
      await onUpdated?.();

      if (editingId === question.id) resetQuestionForm();
    } catch (error) {
      console.error("Erreur suppression question :", error);
      toast.error("Erreur lors de la suppression de la question.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddChoice = async (questionId) => {
    const text = String(choiceForms[questionId] || "").trim();

    if (!text) {
      toast.warn("Saisissez un choix de réponse.");
      return;
    }

    try {
      setSavingChoiceId(questionId);

      await addChoiceToQuestion(questionId, { choice_text: text });

      toast.success("Choix ajouté.");
      setChoiceForms((prev) => ({ ...prev, [questionId]: "" }));
      await fetchQuestions();
    } catch (error) {
      console.error("Erreur ajout choix :", error);
      toast.error("Erreur lors de l’ajout du choix.");
    } finally {
      setSavingChoiceId(null);
    }
  };

  const handleDeleteChoice = async (choice) => {
    if (!choice?.id) return;

    if (!window.confirm("Supprimer ce choix ?")) return;

    try {
      setDeletingId(`choice-${choice.id}`);

      await deleteChoice(choice.id);

      toast.success("Choix supprimé.");
      await fetchQuestions();
    } catch (error) {
      console.error("Erreur suppression choix :", error);
      toast.error("Erreur lors de la suppression du choix.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!isOpen || !vote) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Questions
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaQuestionCircle className="text-indigo-600 dark:text-indigo-300" />
              {vote.title || "Vote sans titre"}
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajoutez des questions, choisissez le type de réponse et gérez les
              choix possibles pour les questions à réponse unique ou multiple.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <FaPlus className="text-indigo-600 dark:text-indigo-300" />
                {editingId ? "Modifier la question" : "Ajouter une question"}
              </p>
            </div>

            <form onSubmit={handleSubmitQuestion} className="space-y-4 p-4">
              <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <ReactQuill
                  key={`${voteId}-${editingId || "create"}`}
                  theme="snow"
                  value={form.question_text}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, question_text: value }))
                  }
                  modules={modules}
                  formats={formats}
                  placeholder="Écrire la question ici..."
                />
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Type de question
                  </label>

                  <select
                    value={form.type}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, type: event.target.value }))
                    }
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  >
                    {QUESTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  {editingId ? (
                    <button
                      type="button"
                      onClick={resetQuestionForm}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  ) : null}

                  <button
                    type="submit"
                    disabled={!canSubmitQuestion}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                  >
                    <FaSave />
                    {savingQuestion
                      ? "Enregistrement..."
                      : editingId
                      ? "Modifier"
                      : "Ajouter"}
                  </button>
                </div>
              </div>
            </form>
          </section>

          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <FaListUl className="text-indigo-600 dark:text-indigo-300" />
                Questions enregistrées
              </p>
            </div>

            {loading ? (
              <div className="space-y-3 p-4">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : questions.length ? (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {questions.map((question, index) => {
                  const isChoiceType =
                    question.type === "single" || question.type === "multiple";

                  return (
                    <article key={question.id} className="p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-sm font-black text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                              {index + 1}
                            </span>

                            <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              {getQuestionTypeLabel(question.type)}
                            </span>

                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {question.choices?.length || 0} choix
                            </span>
                          </div>

                          <div
                            className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
                            dangerouslySetInnerHTML={{
                              __html: question.question_text,
                            }}
                          />
                        </div>

                        <div className="flex shrink-0 justify-end gap-2">
                          <ActionButton
                            title="Modifier"
                            onClick={() => handleEditQuestion(question)}
                            tone="primary"
                          >
                            <FaEdit />
                          </ActionButton>

                          <ActionButton
                            title="Supprimer"
                            onClick={() => handleDeleteQuestion(question)}
                            disabled={deletingId === `question-${question.id}`}
                            tone="danger"
                          >
                            <FaTrash />
                          </ActionButton>
                        </div>
                      </div>

                      {isChoiceType ? (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
                          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                            Choix de réponse
                          </p>

                          <div className="space-y-2">
                            {question.choices?.map((choice) => (
                              <div
                                key={choice.id}
                                className="flex items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
                              >
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                                  {choice.choice_text}
                                </p>

                                <ActionButton
                                  title="Supprimer le choix"
                                  onClick={() => handleDeleteChoice(choice)}
                                  disabled={deletingId === `choice-${choice.id}`}
                                  tone="danger"
                                >
                                  <FaTrash />
                                </ActionButton>
                              </div>
                            ))}

                            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                              <input
                                type="text"
                                value={choiceForms[question.id] || ""}
                                onChange={(event) =>
                                  setChoiceForms((prev) => ({
                                    ...prev,
                                    [question.id]: event.target.value,
                                  }))
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleAddChoice(question.id);
                                  }
                                }}
                                placeholder="Ajouter un choix..."
                                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                              />

                              <button
                                type="button"
                                onClick={() => handleAddChoice(question.id)}
                                disabled={savingChoiceId === question.id}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                              >
                                <FaPlus />
                                Ajouter
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="p-10 text-center">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Aucune question enregistrée pour ce vote.
                </p>
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}